import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import StartingPointEditor from "@/components/StartingPointEditor";
import ScenarioPresets from "@/components/ScenarioPresets";
import SaveGoalButton from "@/components/SaveGoalButton";
import { EXAMPLE, money } from "@/lib/scenarios";
import { Calculator, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, ReferenceLine, LabelList } from "recharts";
import IntroVideo from "@/components/IntroVideo";


function Slider({ label, value, onChange, max, color, testid, note }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-sm font-medium text-neutral-700">{label}</label>
        <span className="font-head text-lg font-semibold" style={{ color }}>{value}%</span>
      </div>
      <input data-testid={testid} type="range" min="0" max={max} step="1" value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[var(--tenant-primary)]" style={{ accentColor: color }} />
      <p className="mt-1 text-xs text-neutral-500" data-testid={`${testid}-dollars`}>{note}</p>
    </div>
  );
}

export default function ProfitCalculator() {
  const [pnl, setPnl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savedGoal, setSavedGoal] = useState(null);

  const start = pnl || EXAMPLE;
  const revenue = start.revenue;
  const cogsPct = revenue > 0 ? (start.cogs / revenue) * 100 : 0;
  const overhead = start.overhead;

  const [revUp, setRevUp] = useState(5);
  const [cogsDown, setCogsDown] = useState(5);
  const [ovhDown, setOvhDown] = useState(5);

  useEffect(() => {
    client.get("/me/goal").then(({ data }) => {
      setPnl(data.pnl);
      if (data.goal) {
        setSavedGoal(data.goal);
        setRevUp(data.goal.rev_up);
        setCogsDown(data.goal.cogs_down);
        setOvhDown(data.goal.ovh_down);
      }
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const scenario = { rev_up: revUp, cogs_down: cogsDown, ovh_down: ovhDown };
  const pickPreset = (p) => { setRevUp(p.rev_up); setCogsDown(p.cogs_down); setOvhDown(p.ovh_down); };
  const isSaved = !!savedGoal && savedGoal.rev_up === revUp && savedGoal.cogs_down === cogsDown && savedGoal.ovh_down === ovhDown;

  const cogs = revenue * (cogsPct / 100);
  const baseProfit = revenue - cogs - overhead;

  const newRevenue = revenue * (1 + revUp / 100);
  const newCogs = newRevenue * (cogsPct / 100) * (1 - cogsDown / 100);
  const newOverhead = overhead * (1 - ovhDown / 100);
  const newProfit = newRevenue - newCogs - newOverhead;

  const revDollars = revenue * (revUp / 100);
  const cogsDollars = newRevenue * (cogsPct / 100) * (cogsDown / 100);
  const ovhDollars = overhead * (ovhDown / 100);
  const extraCogs = revDollars * (cogsPct / 100);
  const delta = newProfit - baseProfit;
  const pctGain = baseProfit !== 0 ? (delta / Math.abs(baseProfit)) * 100 : 0;

  const chartData = [
    { name: "Today", profit: Math.round(baseProfit), fill: "#C9C6BF" },
    { name: "With small moves", profit: Math.round(newProfit), fill: "var(--tenant-primary)" },
  ];

  return (
    <div className="fade-up space-y-8">
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}><Calculator className="tenant-text" strokeWidth={1.5} /></span>
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight text-neutral-900">Profit Growth Calculator</h1>
          <p className="mt-1 max-w-xl text-neutral-600">Small, realistic moves on the Big Three compound into surprising profit. Drag the sliders and watch.</p>
        </div>
      </div>

      <IntroVideo
        testid="intro-video-calculator"
        title="What if your profit doubled?"
        subtitle="Watch Penny & Ollie try the 5-5-5 Strategy with Penny's bakery numbers, then play with YOUR numbers below."
        embedUrl="https://dubb.com/v/WpvrJn/embed?width=auto&height=auto&autoplay=0&no_cta=1&no_controls=0&muted=0"
        paddingBottom="56.10%"
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-7 rounded-2xl border border-[#E5E2DC] bg-white p-7">
          {editing ? (
            <StartingPointEditor initial={{ revenue: start.revenue, cogs: start.cogs, overhead: start.overhead }}
              onSaved={(p) => { setPnl(p); setEditing(false); }} onCancel={() => setEditing(false)} />
          ) : (
            <div className="rounded-xl bg-neutral-50 p-4 text-sm text-neutral-600" data-testid="starting-point" style={{ visibility: loaded ? "visible" : "hidden" }}>
              {!pnl && <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500" data-testid="example-label">Example: Penny's Bakery</p>}
              <p data-testid="starting-point-text">Starting point: {pnl ? "your " : ""}<strong className="text-neutral-800">{money(revenue)}</strong> revenue · <strong className="text-neutral-800">{money(start.cogs)}</strong> COGS ({Math.round(cogsPct)}%) · <strong className="text-neutral-800">{money(overhead)}</strong> overhead.</p>
              {pnl ? (
                <button data-testid="edit-starting-point-btn" onClick={() => setEditing(true)} className="tenant-text mt-2 text-sm font-medium underline-offset-2 hover:underline">Edit starting point</button>
              ) : (
                <button data-testid="use-my-numbers-btn" onClick={() => setEditing(true)} className="mt-3 rounded-full px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ backgroundColor: "var(--tenant-primary)" }}>Use my numbers →</button>
              )}
            </div>
          )}
          <ScenarioPresets current={scenario} onPick={pickPreset} />
          <Slider label="Increase revenue by" value={revUp} onChange={setRevUp} max={100} color="#7A9B76" testid="slider-revenue" note={`+${money(revDollars)} sales (${revUp}%)`} />
          <Slider label="Reduce COGS by" value={cogsDown} onChange={setCogsDown} max={20} color="var(--tenant-primary)" testid="slider-cogs" note={`−${money(cogsDollars)} COGS (${cogsDown}%)`} />
          <Slider label="Reduce overhead by" value={ovhDown} onChange={setOvhDown} max={30} color="#E6B87D" testid="slider-overhead" note={`−${money(ovhDollars)} overhead (${ovhDown}%)`} />
          {revUp > 0 && (
            <p className="rounded-xl bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-600" data-testid="plain-english-breakdown">
              Why these don't simply add up: your +{money(revDollars)} in extra sales also brings about {money(extraCogs)} in extra COGS, so your net gain is {delta >= 0 ? "+" : ""}{money(delta)}.
            </p>
          )}
        </div>

        <div>
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7">
          <div className="mb-2 flex items-center gap-2 text-neutral-500"><TrendingUp size={16} /><span className="text-sm font-medium">Projected annual profit</span></div>
          <div className="flex items-baseline gap-3">
            <p className="font-head text-4xl font-semibold text-neutral-900" data-testid="new-profit">{money(newProfit)}</p>
            <span className="rounded-full px-2.5 py-1 text-sm font-semibold text-white" style={{ backgroundColor: "var(--tenant-primary)" }} data-testid="profit-delta">{delta >= 0 ? "+" : ""}{money(delta)}{baseProfit > 0 ? ` (+${pctGain.toFixed(0)}%)` : ""}</span>
          </div>
          <p className="mt-1 text-sm text-neutral-500" data-testid="profit-pct">
            {baseProfit > 0
              ? `That's a ${pctGain.toFixed(0)}% increase from today's ${money(baseProfit)}.`
              : `Here's your path to profit: from today's ${money(baseProfit)} to ${money(newProfit)} with these moves.`}
          </p>
          <div className="mt-5 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 28, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={13} stroke="#8A8782" />
                <YAxis hide domain={[(min) => Math.min(0, min), (max) => Math.max(0, max)]} />
                <ReferenceLine y={0} stroke="#C9C6BF" />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#FAF9F6" }} />
                <Bar dataKey="profit" radius={[10, 10, 0, 0]} barSize={90}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  <LabelList dataKey="profit" position="top" formatter={money} fontSize={13} fontWeight={600} fill="#1C1B1A" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 rounded-xl p-4 text-sm leading-relaxed text-neutral-700" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
            Notice how a +{money(revDollars)} ({revUp}%) price/revenue lift does more heavy lifting than cost cuts alone — because it flows straight to the bottom line. This is the FISBO "exponential growth" idea in action.
          </p>
          <SaveGoalButton scenario={scenario} isSaved={isSaved} onSaved={setSavedGoal} />
        </div>
        <p className="mt-2 text-xs text-neutral-500" data-testid="profit-disclaimer">This is a 'what if' tool, not a promise. Real growth takes a plan, hard work, and time. Results vary; no guarantees.</p>
        </div>
      </div>
    </div>
  );
}
